<template>
  <div v-if="show" class="au-container container-fluid">
    <div class="row">
      <div class="col">
        <p class="my-4">Upload files</p>

        <div class="dropzone mb-4" v-show="$refs.uploadAdmin && $refs.uploadAdmin.drop">
          <div :class="{ 'dragActive' : $refs.uploadAdmin && $refs.uploadAdmin.dropActive }">
            <h5 v-html="$t('message.Guideline.draganddrop')"></h5>
            <label for="gl" class="btn btn-primary">{{ $t('message.Guideline.selectFiles') }}</label>
          </div>
        </div>

        <file-upload
          ref="uploadAdmin"
          v-model="files"
          :post-action="uploadUrl"
          name="gl"
          :headers="{'Authorization': 'Bearer ' + getAuthToken}"
          chunk-enabled
          :chunk="chunk"
          :drop="true"
          @input-filter="inputFilter"
          @input="$refs.uploadAdmin.active = true"
          :multiple="true">
        </file-upload>

        <template v-if="files && files.length">
          <b-table striped hover :items="files" :fields="['size', {key: 'name', label: $t('message.Guideline.file')}, 'type', 'progress', 'actions']">
            <template slot="info" slot-scope="data">
              <pre>
                {{ data.item.response.data.id }}
              </pre>
            </template>

            <template slot="progress" slot-scope="data">
              <b-progress :value="parseInt(data.item.progress)" :max="100" show-progress animated></b-progress>
            </template>

            <template slot="actions" slot-scope="data">
              <a target="_blank" :href="getFileUrl(data.item)">
                <font-awesome-icon icon="download" size="lg" class="mr-2"/>
              </a>

              <a @click.prevent="deleteFileInfo(data.item, data.index)">
                <font-awesome-icon icon="trash" size="lg" class="mr-2 text-danger"/>
              </a>
            </template>
          </b-table>
        </template>
        <div v-if="!isReviewUpload">
          <b-form-checkbox v-model="increment_version_number" name="gl-increment_version_number">
            <label for="gl-increment_version_number">{{$t('message.Guideline.fileIncrementVersionNumberAsAdmin')}}</label>
          </b-form-checkbox>
        </div>
        <b-button variant="primary" class="float-right" @click="addFilesToGuidelineCommit" :disabled="files.length==0 || ($refs.uploadAdmin && $refs.uploadAdmin.active)">
          {{ submitButtonLabel }}
        </b-button >

        <b-button variant="danger" class="float-right mr-2" @click="$emit('close')">
          {{ $t('message.cancel') }}
        </b-button >
      </div>
    </div>
  </div>
</template>

<script>
import FileUpload from 'vue-upload-component/dist/vue-upload-component.part.js'
import Api from '@/services/Api';
import { GuidelinePhases } from '@/lib/guideline.js';

export default {
  name: 'admin-upload',
  props: ['guideline', 'userId', 'uploadId', 'show', 'isGuidelineSubmitter', 'isReviewUpload'],
  components: {
    FileUpload
  },
  data() {
    return {
      files: [],
      increment_version_number: false,
    }
  },
  computed: {

    // Phase 40 (Writing in process) has no approval step — it is only about
    // uploading the first-draft manuscript, so the primary button reads "Upload
    // files" there. At the review phases it stays "Approve" as before.
    submitButtonLabel() {
      if (this.guideline && this.guideline.phase === GuidelinePhases.preApproved) {
        return this.$t('message.Guideline.upload');
      }
      return this.$t('message.approve');
    },

    chunk() {
      return {
        headers: {'Authorization': 'Bearer ' + this.getAuthToken},
        action: this.uploadChunkedUrl,
        maxActive: 1,
        maxRetries: 5,
      }
    },
  },
  methods: {
    addFilesToGuidelineCommit() {
      Api().post('/guidelines/admin_user_upload_approve/' + this.guideline.id, {
          userId: this.userId,
          uploadId: this.uploadId,
          files: this.files.map(file => {return file.response != undefined ? file.response.data.id : file.id}),
          increment_version_number: this.increment_version_number,
        }).then((response) => {
          // Phase-40 responses report how many manuscript files actually landed.
          // reassigned === 0 means nothing was uploaded successfully — warn and do
          // not advance/reload (phase-50+ responses omit it, so this never fires there).
          const reassigned = response && response.data ? response.data.reassigned : undefined;

          if (reassigned === 0) {
            this.$swal({
              title: this.$t("message.warning"),
              text: this.$t("message.Guideline.noManuscriptUploaded"),
              type: 'warning',
              confirmButtonText: 'OK'
            });
            return;
          }

          let message = this.$t("message.Guideline.fileSuccessfullyUploadedByAdmin")

          if (this.isGuidelineSubmitter) {
            message = this.$t("message.Guideline.fileSuccessfullyUploaded")
          }

          this.$swal({
            title: this.$t("message.success"),
            text: message,
            type: 'success',
            confirmButtonText: 'OK'
          }).then(() => {
            this.$emit('close', 'reload', reassigned);
          });
        })
        .catch(error => {
          this.handleUnauth(error.response.status);
          this.handleForbidden(error.response.status);
        });
    },

    mounded() {
      if (!this.isSuperUser) {
        this.$emit('close');
      }
    },

    getFileUrl(file) {
      return (file.response && file.response.data != undefined) ? file.response.data.url : (file.url!=undefined?file.url:null);
    },

    deleteFileInfo(file) {
      let fileId = file.response != undefined ? file.response.data.id : file.id;

      this.$swal({
        title: this.$t('message.areYouSure'),
        html: this.$t('message.Guideline.deleteFileInfo'),
        type: 'warning',
        showCancelButton: true,
        confirmButtonText: this.$t('message.yesImSure'),
        cancelButtonText: this.$t('message.noCancel'),
      }).then((result) => {
        if (result.value != undefined) {
          this.deleteAdminFile(fileId);
        }
      });
    },

    deleteAdminFile(fileId, fileIndex) {
      Api().get('/delete-file/' + this.guideline.id + '/' + fileId)
        .then( () => {
          this.files.splice(fileIndex, 1);
        })
        .catch( error => {
          this.handleUnauth(error.response.status);
        });
    },
  }
}
</script>

<style lang="scss">
  .au-container{
    width: 100%;
    height: 100vh;
    position: fixed;
    top:0;
    left:0;
    z-index: 1020;
    background: white;
    overflow: hidden;
  }
</style>